/**
 * ProductPopupCardapio - Popup de produto com Combo/Simples, etapas (order_steps) e resumo.
 * Usado em CardapioPublico e Mesas para adicionar itens ao carrinho com a mesma lógica.
 *
 * Props: product, open, onClose, onAddToCart, darkMode, allProducts
 * onAddToCart recebe: { ...product, quantity, observation, combo_type, step_selections, final_price }
 */
import { useState, useEffect, useMemo } from "react";
import { Plus, Minus, X, ChevronLeft, ChevronRight, Check, ShoppingCart, ClipboardList, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ANEL_FOCO, tecladoPopupProduto } from "../lib/tecladoPopupProduto";
import { miniaturaDoProduto } from "../utils/miniatura";
import { sinalDaEtapa, valorDaEtapa } from "../utils/precoEtapas";
import QuantidadePeso, { vendePorPeso } from "./peso/QuantidadePeso";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/uploads/")) return `/api${url}`;
  return `/api${url.startsWith("/") ? url : "/" + url}`;
};

// Fotos pequenas do popup (escolha simples/combo, itens das etapas) em miniatura de
// 400 px — a mesma da grade do cardápio, que o navegador já guardou (25/09/2026)
const fotoPequena = (url) => miniaturaDoProduto(url, 400) || getImageUrl(url);

const isPngImage = (url) => {
  if (!url) return false;
  return url.toLowerCase().endsWith(".png");
};

// `modulo` (26/10/2026): Mesas e Comandas passam o módulo para o quadro de peso
// ler a balança do computador (features/balanca). O cardápio do cliente não passa.
export default function ProductPopupCardapio({ product, open, onClose, onAddToCart, darkMode = false, allProducts = [], activePromotions = [], initialData = null, modulo = null }) {
  const [imageError, setImageError] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [pesadoBalanca, setPesadoBalanca] = useState(false);
  const [observation, setObservation] = useState("");
  const [comboStep, setComboStep] = useState(0);
  const [selectedComboType, setSelectedComboType] = useState(null);
  const [stepSelections, setStepSelections] = useState({});
  const [itemImageErrors, setItemImageErrors] = useState({});
  const [stepSearch, setStepSearch] = useState("");

  const initializeDefaultSelections = (steps, filterComboOnly = false, comboType = null) => {
    const initialSelections = {};
    let relevantSteps = steps;
    if (filterComboOnly && comboType === "simples") {
      relevantSteps = steps.filter((step) => !step.combo_only);
    }
    relevantSteps.forEach((step, index) => {
      if (step.default_item_id) {
        const itemExists = step.items?.some((item) => item.product_id === step.default_item_id);
        if (itemExists) initialSelections[index] = [step.default_item_id];
      }
    });
    return initialSelections;
  };

  useEffect(() => {
    if (open && product) {
      // `initialData` (v1.5.469): edição de item do carrinho — o popup abre
      // com as escolhas feitas (quantidade, observação, tipo de combo e
      // etapas) em vez de zerado. Sem a prop, comportamento idêntico ao de
      // sempre: default null não muda nada pro Cardápio público e Delivery.
      setQuantity(initialData?.quantity || 1);
      setPesadoBalanca(false);
      setObservation(initialData?.observation || "");
      setImageError(false);
      setSelectedComboType(initialData?.combo_type || null);
      setItemImageErrors({});
      setStepSearch("");
      const isComboProduct = product.product_type === "combo";
      const hasSteps = product.order_steps && product.order_steps.length > 0;
      const temSelecoes = initialData?.step_selections
        && Object.keys(initialData.step_selections).length > 0;
      if (temSelecoes) {
        setStepSelections(initialData.step_selections);
        setComboStep(1); // já configurado: cai direto nas etapas preenchidas
      } else if (!isComboProduct && hasSteps) {
        setStepSelections(initializeDefaultSelections(product.order_steps));
        setComboStep(1);
      } else {
        setStepSelections({});
        setComboStep(isComboProduct ? 0 : 0);
      }
    }
  }, [open, product, initialData]);

  // Limpar busca quando mudar de etapa
  useEffect(() => {
    setStepSearch("");
  }, [comboStep]);

  const handleSelectComboType = (type) => {
    setSelectedComboType(type);
    if (product?.order_steps?.length > 0) {
      setStepSelections(initializeDefaultSelections(product.order_steps, true, type));
    }
  };

  if (!product) return null;

  const isCombo = product.product_type === "combo";
  const hasOrderSteps = product.order_steps && product.order_steps.length > 0;
  const comboPrice = product.sale_price || 0;
  const simplePrice = product.simple_price || Math.round(comboPrice * 0.7 * 100) / 100;

  // Promoções
  const _getPromo = (comboType) => {
    for (const promo of activePromotions) {
      const pp = (promo.products || []).find(p => p.product_id === product.id);
      if (pp) {
        if (comboType === 'combo' && pp.discount_value_combo) return { value: pp.discount_value_combo, type: promo.discount_type };
        return { value: pp.discount_value, type: promo.discount_type };
      }
    }
    return null;
  };
  const _calcPromo = (orig, info) => {
    if (!info) return null;
    if (info.type === 'percentage') return orig * (1 - info.value / 100);
    if (info.type === 'fixed_price') return info.value;
    return null;
  };
  const promoSimple = _getPromo('simples');
  const promoCombo = _getPromo('combo');
  const simplePricePromo = isCombo ? _calcPromo(simplePrice, promoSimple) : _calcPromo(comboPrice, promoSimple);
  const comboPricePromo = _calcPromo(comboPrice, promoCombo);

  const getRelevantSteps = () => {
    if (!hasOrderSteps) return [];
    return product.order_steps.filter((step) => {
      if (selectedComboType === "simples") return !step.combo_only;
      return true;
    });
  };

  const relevantSteps = getRelevantSteps();
  const totalSteps = relevantSteps.length;
  const totalStepsWithSummary = totalSteps + 1;
  const isOnSummaryStep = comboStep > totalSteps && comboStep > 0;

  const getProductPhoto = (id) => allProducts.find((p) => p.id === id)?.photo_url || null;
  const isProductAvailable = (id) => allProducts.find((p) => p.id === id)?.available !== false;
  const getProductPrice = (id) => allProducts.find((p) => p.id === id)?.sale_price || 0;
  const getProductDescription = (id) => allProducts.find((p) => p.id === id)?.description || "";

  const getItemPrice = (item, step) => {
    // Defensivo: `step.items?.find()` retorna undefined quando o itemId em
    // `stepSelections` não existe mais em `step.items` (produto foi removido
    // do cardápio, step editado, cache antigo). Sem guard, quebra a tela.
    if (!item) return 0;
    if (step?.sync_prices !== false) {
      const p = getProductPrice(item.product_id);
      if (p > 0) return p;
      // Fallback: se o produto referenciado nao aparece em allProducts (ex.: is_insumo=true,
      // adicionais ocultos do cardapio publico), usa price_override pra nao zerar o preco.
      if (Number(item.price_override) > 0) return Number(item.price_override);
      return 0;
    }
    if (item.price_override === 0) return 0;
    if (item.price_override > 0) return item.price_override;
    return getProductPrice(item.product_id);
  };

  const calculateTotalPrice = () => {
    let basePrice;
    if (!isCombo) {
      basePrice = simplePricePromo != null ? simplePricePromo : comboPrice;
    } else {
      basePrice = selectedComboType === "combo" ? (comboPricePromo != null ? comboPricePromo : comboPrice) : (simplePricePromo != null ? simplePricePromo : simplePrice);
    }
    if (hasOrderSteps) {
      relevantSteps.forEach((step, index) => {
        const selectedItems = stepSelections[index] || [];
        // Soma, Máximo ou Subtração: regra única em utils/precoEtapas.js
        const precos = selectedItems
          .map((itemId) => step.items?.find((i) => i.product_id === itemId))
          .filter(Boolean)
          .map((item) => getItemPrice(item, step));
        basePrice += valorDaEtapa(step, precos);
      });
    }
    return Math.max(0, basePrice) * quantity;
  };

  const totalPrice = calculateTotalPrice();

  const canAdvanceStep = () => {
    if (comboStep === 0) return selectedComboType !== null;
    if (isOnSummaryStep) return true;
    const currentStep = relevantSteps[comboStep - 1];
    if (!currentStep) return true;
    const selections = stepSelections[comboStep - 1] || [];
    return selections.length >= (currentStep.min_selections || 0);
  };

  const handleNextStep = () => {
    if (comboStep === 0) setComboStep(1);
    else if (comboStep <= totalSteps) setComboStep(comboStep + 1);
    else handleAdd();
  };

  const toggleItemSelection = (stepIndex, itemId) => {
    const step = relevantSteps[stepIndex];
    if (!step) return;
    const current = stepSelections[stepIndex] || [];
    const isSelected = current.includes(itemId);
    const maxSelections = step.max_selections || 999;
    if (isSelected) {
      setStepSelections({ ...stepSelections, [stepIndex]: current.filter((id) => id !== itemId) });
    } else {
      if (maxSelections === 1) setStepSelections({ ...stepSelections, [stepIndex]: [itemId] });
      else if (current.length < maxSelections) setStepSelections({ ...stepSelections, [stepIndex]: [...current, itemId] });
    }
  };

  const getSelectionsSummary = () => {
    const summary = [];
    relevantSteps.forEach((step, index) => {
      const selectedItems = stepSelections[index] || [];
      const itemsWithPrices = selectedItems
        .map((itemId) => {
          const item = step.items?.find((i) => i.product_id === itemId);
          if (!item) return null;  // stepSelections com id órfão (produto removido do step)
          return { name: item.product_name || "", price: getItemPrice(item, step) };
        })
        .filter((i) => i && i.name);
      if (itemsWithPrices.length > 0) summary.push({ stepName: step.name, items: itemsWithPrices, stepIndex: index, sinal: sinalDaEtapa(step) });
    });
    return summary;
  };

  const handleAdd = () => {
    onAddToCart({
      ...product,
      quantity,
      observation: observation.trim() || null,
      combo_type: isCombo ? selectedComboType : null,
      step_selections: stepSelections,
      order_steps: product.order_steps,
      final_price: totalPrice / quantity,
      ...(pesadoBalanca ? { pesado_balanca: true } : {}),
    });
    onClose();
  };

  const t = {
    bg: darkMode ? "bg-zinc-900" : "bg-white",
    bgMuted: darkMode ? "bg-zinc-800" : "bg-gray-100",
    bgCard: darkMode ? "bg-zinc-800/80" : "bg-white",
    bgInput: darkMode ? "bg-zinc-700" : "bg-gray-50",
    text: darkMode ? "text-zinc-100" : "text-gray-900",
    textMuted: darkMode ? "text-zinc-400" : "text-gray-500",
    border: darkMode ? "border-zinc-600" : "border-gray-200",
    selectedBg: darkMode ? "bg-orange-500/20" : "bg-orange-50",
    selectedBorder: "border-orange-500",
    hoverBorder: darkMode ? "hover:border-orange-400" : "hover:border-orange-300",
  };

  // --- TELA COMBO vs SIMPLES ---
  if (isCombo && comboStep === 0) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent {...tecladoPopupProduto()} hideClose className={`sm:max-w-xl p-0 gap-0 overflow-hidden ${t.bg} border-0 rounded-2xl w-[95vw] sm:w-full`}>
          <DialogHeader className="sr-only"><DialogTitle>{product.name}</DialogTitle></DialogHeader>
          <div className={`p-4 border-b ${t.border}`}>
            <button onClick={onClose} className={`absolute top-3 right-3 w-8 h-8 rounded-full ${darkMode ? "bg-zinc-700 hover:bg-zinc-600" : "bg-gray-100 hover:bg-gray-200"} flex items-center justify-center z-10`}>
              <X className={`w-5 h-5 ${t.text}`} />
            </button>
            <div className="pr-10">
              <h2 className={`text-xl font-bold ${t.text}`}>{product.name}</h2>
              {product.description && <p className={`text-sm ${t.textMuted} mt-1`}>{product.description}</p>}
              <p className={`text-xs ${t.textMuted} mt-3 font-medium`}>Escolha como quer seu pedido:</p>
            </div>
          </div>
          <div className="p-4 flex gap-3 items-center">
            <button onClick={() => handleSelectComboType("simples")} data-opcao data-escolhida={selectedComboType === "simples" ? "1" : "0"} className={`${ANEL_FOCO} w-[35%] flex-shrink-0 rounded-xl border-2 transition-all text-left overflow-hidden flex flex-col ${selectedComboType === "simples" ? `${t.selectedBorder} ${t.selectedBg}` : `${t.border} ${darkMode ? "bg-zinc-800" : "bg-white"} ${t.hoverBorder}`}`}>
              <div className={`w-full aspect-square ${darkMode ? "bg-gradient-to-b from-zinc-700 to-zinc-800" : "bg-gradient-to-b from-orange-50 to-orange-100"}`}>
                {(product.simple_photo_url || product.photo_url) && !imageError ? <img src={fotoPequena(product.simple_photo_url || product.photo_url)} alt="" className="w-full h-full object-contain p-2" /> : <div className="w-full h-full flex items-center justify-center text-orange-300"><span className="text-3xl">🍽️</span></div>}
              </div>
              <div className="p-2 text-center">
                <p className={`font-bold ${t.text} text-xs`}>SIMPLES</p>
                <p className={`text-[9px] ${t.textMuted}`}>{product.simple_description || "Apenas o produto"}</p>
                {simplePricePromo != null && simplePricePromo !== simplePrice ? (<><p className={`text-xs line-through ${t.textMuted} mt-1`}>R$ {simplePrice.toFixed(2).replace(".", ",")}</p><p className="text-base font-black text-green-500">R$ {simplePricePromo.toFixed(2).replace(".", ",")}</p></>) : (<p className="text-base font-black text-orange-500 mt-1">R$ {simplePrice.toFixed(2).replace(".", ",")}</p>)}
              </div>
            </button>
            <button onClick={() => handleSelectComboType("combo")} data-opcao data-escolhida={selectedComboType === "combo" ? "1" : "0"} className={`${ANEL_FOCO} w-[65%] flex-shrink-0 rounded-2xl border-2 transition-all text-left overflow-hidden flex flex-col relative ${selectedComboType === "combo" ? `${t.selectedBorder} ${t.selectedBg}` : `${t.border} ${darkMode ? "bg-zinc-800" : "bg-white"} ${t.hoverBorder}`}`}>
              <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold py-1.5 text-center z-10 flex items-center justify-center gap-1.5">★ RECOMENDADO ★</div>
              <div className={`w-full aspect-square pt-8 ${darkMode ? "bg-gradient-to-b from-zinc-700 to-zinc-800" : "bg-gradient-to-b from-orange-100 to-orange-200"}`}>
                {(product.combo_photo_url || product.photo_url) && !imageError ? <img src={fotoPequena(product.combo_photo_url || product.photo_url)} alt="" className="w-full h-full object-contain p-2" /> : <div className="w-full h-full flex items-center justify-center text-orange-400"><span className="text-5xl">🍽️</span></div>}
              </div>
              <div className="p-3">
                <p className={`font-bold ${t.text} text-sm`}>COMBO</p>
                <p className={`text-[10px] ${t.textMuted} line-clamp-1`}>{product.combo_description || "+ Batata + Refrigerante"}</p>
                {comboPricePromo != null && comboPricePromo !== comboPrice ? (<><p className={`text-sm line-through ${t.textMuted} mt-2`}>R$ {comboPrice.toFixed(2).replace(".", ",")}</p><p className="text-xl font-black text-green-500">R$ {comboPricePromo.toFixed(2).replace(".", ",")}</p></>) : (<p className="text-xl font-black text-orange-500 mt-2">R$ {comboPrice.toFixed(2).replace(".", ",")}</p>)}
              </div>
            </button>
          </div>
          <div className={`p-4 ${t.bgMuted} border-t ${t.border}`}>
            <button onClick={handleNextStep} data-avancar disabled={!canAdvanceStep()} className={`w-full h-12 rounded-full font-bold text-white flex items-center justify-center gap-2 ${canAdvanceStep() ? "bg-orange-500 hover:bg-orange-600" : "bg-gray-400 cursor-not-allowed"}`}>
              {selectedComboType && hasOrderSteps && product.order_steps.filter((s) => (selectedComboType === "simples" ? !s.combo_only : true)).length > 0 ? <>PRÓXIMA ETAPA <ChevronRight className="w-5 h-5" /></> : <>Adicionar • R$ {(selectedComboType === "combo" ? comboPrice : simplePrice).toFixed(2).replace(".", ",")}</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // --- TELA DE RESUMO ---
  const shouldShowSummary = (isCombo || (!isCombo && hasOrderSteps)) && isOnSummaryStep;
  if (shouldShowSummary) {
    const selectionsSummary = getSelectionsSummary();
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent {...tecladoPopupProduto()} hideClose className={`sm:max-w-md p-0 gap-0 overflow-hidden ${t.bg} border-0 rounded-2xl w-[92vw] sm:w-full max-h-[90vh] flex flex-col`}>
          <DialogHeader className="sr-only"><DialogTitle>Confirme seu pedido</DialogTitle></DialogHeader>
          <div className={`p-4 border-b ${t.border}`}>
            <div className="flex items-center justify-between mb-2">
              <button onClick={() => setComboStep(totalSteps > 0 ? totalSteps : 0)} data-voltar className={`w-8 h-8 rounded-full ${darkMode ? "bg-zinc-700" : "bg-gray-100"} flex items-center justify-center`}><ChevronLeft className={`w-5 h-5 ${t.text}`} /></button>
              <span className={`text-sm ${t.textMuted}`}>Resumo do Pedido</span>
              <button onClick={onClose} className={`w-8 h-8 rounded-full ${darkMode ? "bg-zinc-700" : "bg-gray-100"} flex items-center justify-center`}><X className={`w-5 h-5 ${t.text}`} /></button>
            </div>
            <h2 className={`text-lg font-bold ${t.text} text-center`}>Confirme seu pedido</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="flex gap-4 items-center">
              <div className={`w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 ${darkMode ? "bg-zinc-700" : "bg-orange-50"}`}>
                {(selectedComboType === "combo" ? (product.combo_photo_url || product.photo_url) : (product.simple_photo_url || product.photo_url)) && !imageError ? <img src={fotoPequena(selectedComboType === "combo" ? (product.combo_photo_url || product.photo_url) : (product.simple_photo_url || product.photo_url))} alt="" className="w-full h-full object-contain" onError={() => setImageError(true)} /> : <div className="w-full h-full flex items-center justify-center text-orange-300"><span className="text-3xl">🍽️</span></div>}
              </div>
              <div className="flex-1">
                <h3 className={`font-bold text-lg ${t.text}`}>{product.name}</h3>
                {product.description && <p className={`text-xs ${t.textMuted} mt-1 line-clamp-2`}>{product.description}</p>}
              </div>
            </div>
            <div className={`${darkMode ? "bg-zinc-800" : "bg-gray-100"} rounded-xl p-4 space-y-3`}>
              <h4 className={`font-semibold text-sm ${t.text} flex items-center gap-2`}><ClipboardList className="w-4 h-4" /> Suas Escolhas</h4>
              {isCombo && (() => {
                const typePrice = selectedComboType === "combo" ? comboPrice : simplePrice;
                // Não renderiza a linha quando o preço base é 0 — o produto cobra via etapas.
                if (!typePrice || typePrice <= 0) return null;
                return (
                  <div className={`flex justify-between items-center py-2 border-b ${t.border}`}>
                    <div>
                      <p className={`text-xs ${t.textMuted}`}>Tipo</p>
                      <p className={`text-sm font-semibold ${t.text}`}>{selectedComboType === "combo" ? "🍟 COMBO" : "SIMPLES"}</p>
                    </div>
                    <span className="text-sm font-bold text-orange-500">R$ {typePrice.toFixed(2).replace(".", ",")}</span>
                  </div>
                );
              })()}
              {selectionsSummary.map((sel, idx) => (
                <div key={idx} className={`py-2 ${idx < selectionsSummary.length - 1 ? `border-b ${t.border}` : ""}`}>
                  <div className="flex justify-between items-start mb-1"><p className={`text-xs ${t.textMuted}`}>{sel.stepName}</p><button onClick={() => setComboStep(sel.stepIndex + 1)} className="text-orange-500 text-xs underline">Alterar</button></div>
                  {sel.items.map((it, i) => <div key={i} className="flex justify-between items-center"><p className={`text-sm ${t.text}`}>{it.name}</p>{it.price > 0 ? <span className="text-sm font-semibold text-orange-500">{sel.sinal || "+"}R$ {it.price.toFixed(2).replace(".", ",")}</span> : <span className="text-xs text-green-500 font-bold uppercase">Grátis</span>}</div>)}
                </div>
              ))}
            </div>
            <div>
              <label className={`text-sm font-medium mb-2 block ${t.text}`}>Observação (opcional)</label>
              <textarea value={observation} onChange={(e) => setObservation(e.target.value)} placeholder="Ex: sem cebola, molho à parte..." className={`w-full resize-none rounded-xl p-3 text-sm ${darkMode ? "bg-zinc-700" : "bg-white"} ${t.text} border ${t.border}`} maxLength={200} rows={3} />
            </div>
            <div className={`${darkMode ? "bg-zinc-800" : "bg-gray-100"} rounded-xl p-4`}>
              <p className={`text-sm font-medium ${t.text} text-center mb-3`}>
                Quantidade{vendePorPeso(product) && <span className={`text-xs ${t.textMuted} ml-1`}>(em {product.sale_unit})</span>}
              </p>
              {/* Quilo/litro com etapa (ex.: Acém com "Escolha seu corte"): até 02/10/2026
                  aqui só andava de 1 em 1 — não dava para lançar 500 g na mesa. */}
              {vendePorPeso(product) ? (
                <QuantidadePeso product={product} quantity={quantity} setQuantity={setQuantity} variante={darkMode ? "escuro" : "claro"} modulo={modulo} setPesadoBalanca={setPesadoBalanca} />
              ) : (
              <div className="flex items-center justify-center gap-4">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1} className={`w-12 h-12 rounded-full ${darkMode ? "bg-orange-900/30" : "bg-orange-100"} flex items-center justify-center ${quantity <= 1 ? "opacity-40" : ""}`}><Minus className={`w-5 h-5 ${darkMode ? "text-orange-400" : "text-orange-600"}`} /></button>
                <span className={`text-3xl font-bold ${t.text} w-16 text-center`}>{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center hover:bg-orange-600"><Plus className="w-5 h-5 text-white" /></button>
              </div>
              )}
            </div>
          </div>
          <div className={`p-4 ${t.bgMuted} border-t ${t.border}`}>
            <button onClick={handleAdd} data-avancar className="w-full h-14 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg flex items-center justify-center gap-2"><ShoppingCart className="w-5 h-5" /> Adicionar • R$ {totalPrice.toFixed(2).replace(".", ",")}</button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // --- TELA DE ETAPAS (order_steps) ---
  const shouldShowSteps = (isCombo && comboStep > 0 && comboStep <= totalSteps) || (!isCombo && hasOrderSteps && comboStep > 0 && comboStep <= totalSteps);
  if (shouldShowSteps && relevantSteps.length > 0) {
    const currentStepIndex = comboStep - 1;
    const currentStep = relevantSteps[currentStepIndex];
    const selections = stepSelections[currentStepIndex] || [];
    if (!currentStep) {
      setComboStep(totalSteps + 1);
      return null;
    }
    // Filtrar itens pela busca + ordenar empurrando esgotados pro fim (sort estável preserva a ordem original dos disponíveis)
    // Remove items cujos produtos foram marcados como 'escondido' no Produtos→Cardápio,
    // OU foram arquivados/deletados (não vêm mais em /products). A lógica anterior
    // usava `!prod ||` o que MANTINHA o item quando o produto não existia — bug.
    const rawItems = (currentStep.items || []).filter((it) => {
      const prod = allProducts.find((p) => p.id === it.product_id);
      return prod && prod.hidden !== true;
    });
    const allItems = [...rawItems].sort((a, b) => {
      const aOut = isProductAvailable(a.product_id) ? 0 : 1;
      const bOut = isProductAvailable(b.product_id) ? 0 : 1;
      return aOut - bOut;
    });
    const showSearch = allItems.length > 10;
    const filteredItems = stepSearch.trim()
      ? allItems.filter((item) => (item.product_name || "").toLowerCase().includes(stepSearch.toLowerCase()))
      : allItems;
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent {...tecladoPopupProduto()} hideClose className={`sm:max-w-md p-0 gap-0 overflow-hidden ${t.bg} border-0 rounded-2xl w-[92vw] sm:w-full max-h-[90vh] flex flex-col`}>
          <DialogHeader className="sr-only"><DialogTitle>{currentStep.name || `Etapa ${comboStep}`}</DialogTitle></DialogHeader>
          <div className={`p-4 border-b ${t.border}`}>
            <div className="flex items-center justify-between mb-2">
              <button onClick={() => setComboStep(comboStep - 1)} data-voltar className={`w-8 h-8 rounded-full ${darkMode ? "bg-zinc-700" : "bg-gray-100"} flex items-center justify-center`}><ChevronLeft className={`w-5 h-5 ${t.text}`} /></button>
              <span className={`text-sm ${t.textMuted}`}>Etapa {comboStep} de {totalStepsWithSummary}</span>
              <button onClick={onClose} className={`w-8 h-8 rounded-full ${darkMode ? "bg-zinc-700" : "bg-gray-100"} flex items-center justify-center`}><X className={`w-5 h-5 ${t.text}`} /></button>
            </div>
            <h2 className={`text-lg font-bold ${t.text} text-center`}>{currentStep.name || `Etapa ${comboStep}`}</h2>
            {(currentStep.min_selections > 0 || currentStep.max_selections > 0) && <p className={`text-xs ${t.textMuted} text-center mt-1`}>Selecione {currentStep.min_selections > 0 ? `de ${currentStep.min_selections} a ${currentStep.max_selections || "?"} opções` : "até " + (currentStep.max_selections || "?") + " opções"}</p>}
            {showSearch && (
              <div data-busca-opcao className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-lg ${darkMode ? "bg-zinc-700" : "bg-gray-100"}`}>
                <Search className={`w-4 h-4 ${t.textMuted}`} />
                <input
                  type="text"
                  value={stepSearch}
                  onChange={(e) => setStepSearch(e.target.value)}
                  placeholder="Buscar opção..."
                  className={`flex-1 bg-transparent border-none outline-none text-sm ${t.text}`}
                />
                {stepSearch && (
                  <button onClick={() => setStepSearch("")} className={`p-1 rounded-full ${darkMode ? "hover:bg-zinc-600" : "hover:bg-gray-200"}`}>
                    <X className={`w-3 h-3 ${t.textMuted}`} />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="p-3 flex-1 overflow-y-auto">
            {filteredItems.length === 0 ? (
              <p className={`text-center ${t.textMuted} py-8`}>Nenhuma opção encontrada</p>
            ) : (
            <div className="grid grid-cols-3 gap-2">
              {filteredItems.map((item) => {
                const isSelected = selections.includes(item.product_id);
                const itemPhoto = getProductPhoto(item.product_id);
                const hasErr = itemImageErrors[item.product_id];
                const available = isProductAvailable(item.product_id);
                return (
                  <button key={item.product_id} onClick={() => available && toggleItemSelection(currentStepIndex, item.product_id)} disabled={!available} data-opcao data-escolhida={isSelected ? "1" : "0"} className={`${ANEL_FOCO} relative p-1.5 rounded-lg border-2 text-left flex flex-col ${!available ? "opacity-60 cursor-not-allowed border-gray-300" : isSelected ? `${t.selectedBorder} ${t.selectedBg}` : `${t.border} ${t.bgCard} ${t.hoverBorder}`}`}>
                    {isSelected && available && <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center z-10"><Check className="w-3 h-3 text-white" /></div>}
                    <div className={`w-full aspect-square rounded-md overflow-hidden mb-1.5 ${!available ? "grayscale" : ""}`}>
                      {itemPhoto && !hasErr ? <img src={fotoPequena(itemPhoto)} alt="" className={`w-full h-full ${isPngImage(itemPhoto) ? "object-contain p-1" : "object-cover"}`} onError={() => setItemImageErrors((p) => ({ ...p, [item.product_id]: true }))} /> : <div className="w-full h-full flex items-center justify-center bg-muted"><span className="text-2xl">🍽️</span></div>}
                      {!available && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><span className="text-white text-[10px] font-bold bg-red-500 px-1.5 py-0.5 rounded">ESGOTADO</span></div>}
                    </div>
                    <p className={`font-medium text-xs ${t.text} line-clamp-2 leading-tight ${!available ? "line-through opacity-60" : ""}`} title={item.product_name}>{item.product_name}</p>
                    {getProductDescription(item.product_id) && <p className={`text-[10px] ${t.textMuted} line-clamp-2 mt-0.5`}>{getProductDescription(item.product_id)}</p>}
                    {available && (getItemPrice(item, currentStep) > 0 ? <p className="text-xs text-orange-500 font-bold mt-0.5">{sinalDaEtapa(currentStep)}R$ {getItemPrice(item, currentStep).toFixed(2).replace(".", ",")}</p> : <p className="text-[10px] text-green-500 font-bold mt-0.5 uppercase">Grátis</p>)}
                    {!available && <p className="text-[10px] text-red-500 font-bold mt-0.5">ESGOTADO</p>}
                  </button>
                );
              })}
            </div>
            )}
          </div>
          <div className={`p-4 ${t.bgMuted} border-t ${t.border}`}>
            <button onClick={handleNextStep} data-avancar disabled={!canAdvanceStep()} className={`w-full h-12 rounded-full font-bold text-white flex items-center justify-center gap-2 ${canAdvanceStep() ? "bg-orange-500 hover:bg-orange-600" : "bg-gray-400 cursor-not-allowed"}`}>
              {comboStep < totalSteps ? <>PRÓXIMA ETAPA <ChevronRight className="w-5 h-5" /></> : <>REVISAR PEDIDO <ChevronRight className="w-5 h-5" /></>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // --- TELA PADRÃO (produto simples ou combo sem etapas) ---
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent {...tecladoPopupProduto()} hideClose className={`sm:max-w-md p-0 gap-0 overflow-hidden ${t.bg} border-0 rounded-2xl w-[92vw] sm:w-full max-h-[90vh] flex flex-col`}>
        <DialogHeader className="sr-only"><DialogTitle>{product.name}</DialogTitle></DialogHeader>
        <div className={`relative w-full aspect-square ${vendePorPeso(product) ? "max-h-[30vh] flex-shrink-0" : ""} ${darkMode ? "bg-gradient-to-b from-zinc-800 to-zinc-900" : "bg-gradient-to-b from-orange-100 to-orange-50"}`}>
          {product.photo_url && !imageError ? <img src={getImageUrl(product.photo_url)} alt="" className="w-full h-full object-contain" onError={() => setImageError(true)} /> : <div className="w-full h-full flex items-center justify-center text-orange-300"><span className="text-6xl">🍽️</span></div>}
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center"><X className="w-5 h-5 text-white" /></button>
        </div>
        <div className="p-4 sm:p-5 overflow-y-auto flex-1">
          <h2 className={`text-xl font-bold ${t.text} mb-1`}>{product.name}</h2>
          {product.description && <p className={`${t.textMuted} text-sm`}>{product.description}</p>}
          {product.product_type === "combo" ? (
            <div className="flex justify-center items-start gap-8 mb-5 mt-4">
              <div className="flex flex-col items-center"><span className={`text-xs ${t.textMuted} uppercase`}>Individual</span>{simplePricePromo != null && simplePricePromo !== simplePrice ? (<><span className={`text-sm line-through ${t.textMuted}`}>R$ {simplePrice.toFixed(2).replace(".", ",")}</span><span className="text-2xl font-bold text-green-500">R$ {simplePricePromo.toFixed(2).replace(".", ",")}</span></>) : (<span className="text-2xl font-bold text-orange-500">R$ {simplePrice.toFixed(2).replace(".", ",")}</span>)}</div>
              <div className="flex flex-col items-center"><span className={`text-xs ${t.textMuted} uppercase`}>Combo</span>{comboPricePromo != null && comboPricePromo !== comboPrice ? (<><span className={`text-sm line-through ${t.textMuted}`}>R$ {comboPrice.toFixed(2).replace(".", ",")}</span><span className="text-2xl font-bold text-green-500">R$ {comboPricePromo.toFixed(2).replace(".", ",")}</span></>) : (<span className="text-2xl font-bold text-orange-500">R$ {comboPrice.toFixed(2).replace(".", ",")}</span>)}</div>
            </div>
          ) : <div className="mb-5">{(() => {
              const unitSuffix = product.sale_unit && product.sale_unit !== "un"
                ? <span className={`text-sm font-normal ${t.textMuted} ml-1`}>/{product.sale_unit}</span>
                : null;
              return simplePricePromo != null && simplePricePromo !== comboPrice ? (
                <div className="flex items-center justify-center gap-2"><span className={`text-lg line-through ${t.textMuted}`}>R$ {comboPrice.toFixed(2).replace(".", ",")}</span><span className="text-2xl font-bold text-green-500">R$ {simplePricePromo.toFixed(2).replace(".", ",")}{unitSuffix}</span></div>
              ) : (
                <div className="text-2xl font-bold text-orange-500">R$ {comboPrice.toFixed(2).replace(".", ",")}{unitSuffix}</div>
              );
            })()}</div>}
          {/* Quilo/litro: os mesmos botões do cardápio, do Balcão e do Delivery (02/10/2026). */}
          {vendePorPeso(product) && (
            <div className={`mb-4 ${darkMode ? "bg-zinc-800" : "bg-gray-100"} rounded-xl p-4`} data-e2e="mesas-popup-peso">
              <p className={`text-sm font-medium ${t.text} text-center mb-3`}>
                Quantidade <span className={`text-xs ${t.textMuted} ml-1`}>(em {product.sale_unit})</span>
              </p>
              <QuantidadePeso product={product} quantity={quantity} setQuantity={setQuantity} variante={darkMode ? "escuro" : "claro"} modulo={modulo} setPesadoBalanca={setPesadoBalanca} />
            </div>
          )}
          <div>
            <label className={`text-sm font-medium mb-1.5 block ${t.textMuted}`}>Observação (opcional)</label>
            <textarea value={observation} onChange={(e) => setObservation(e.target.value)} placeholder="Ex: sem cebola, molho à parte..." className={`w-full resize-none rounded-xl p-3 text-sm ${darkMode ? "bg-zinc-800" : "bg-gray-100"} ${t.text} border-0`} maxLength={200} rows={2} />
          </div>
        </div>
        <div className={`p-4 ${t.bgMuted} flex items-center gap-3`}>
          {vendePorPeso(product) ? null : product.is_divisible ? (() => {
            const step = (product.sale_unit === "kg" || product.sale_unit === "L") ? 0.1 : 0.5;
            const unitLabel = product.sale_unit && product.sale_unit !== "un" ? product.sale_unit : null;
            return (
              <div className={`flex gap-0 ${t.bg} rounded-full border ${t.border} overflow-hidden items-center`}>
                <button onClick={() => setQuantity(Math.max(0.001, parseFloat((quantity - step).toFixed(3))))} className={`w-10 h-10 flex items-center justify-center`}><Minus className={`w-4 h-4 ${t.text}`} /></button>
                <div className="flex items-center px-1">
                  <input
                    type="number"
                    step="0.001"
                    min="0.001"
                    value={quantity}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v) && v > 0) setQuantity(v);
                    }}
                    className={`w-14 text-center font-bold bg-transparent border-0 outline-none ${t.text}`}
                  />
                  {unitLabel && <span className={`text-[10px] ${t.textMuted} font-normal`}>{unitLabel}</span>}
                </div>
                <button onClick={() => setQuantity(parseFloat((quantity + step).toFixed(3)))} className="w-10 h-10 flex items-center justify-center"><Plus className={`w-4 h-4 ${t.text}`} /></button>
              </div>
            );
          })() : (
            <div className={`flex gap-0 ${t.bg} rounded-full border ${t.border} overflow-hidden`}>
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1} className={`w-10 h-10 flex items-center justify-center ${quantity <= 1 ? "opacity-30" : ""}`}><Minus className={`w-4 h-4 ${t.text}`} /></button>
              <span className={`w-8 text-center font-bold ${t.text}`}>{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 flex items-center justify-center"><Plus className={`w-4 h-4 ${t.text}`} /></button>
            </div>
          )}
          {!isCombo && hasOrderSteps && comboStep === 0 ? (
            <button data-avancar onClick={() => { setStepSelections(initializeDefaultSelections(product.order_steps)); setComboStep(1); }} className="flex-1 h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm flex items-center justify-center gap-2">Personalizar <ChevronRight className="w-4 h-4" /></button>
          ) : (
            <button onClick={handleAdd} data-avancar className="flex-1 h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm flex items-center justify-center gap-2">Adicionar • R$ {totalPrice.toFixed(2).replace(".", ",")}</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
